import { Mileage, PriceIntervalEnum } from "@finway-group/shared/lib/models"
import { Avatar, Card, Col, Row, Space, Steps, Tooltip } from "antd"
import { HoverableInfo } from "Components/forms/expenseForm/reimbursementForms/auxilliary/HoverableInfo"
import { getVehicleString, isDistanceOverThreshold } from "Components/forms/expenseForm/reimbursementForms/mileage/helpers"
import PriceLabel from "Components/priceLabel"
import moment from "moment"
import React from "react"
import { MapPin } from "react-feather"
import { useTranslation } from "react-i18next"
import { useCompany } from "Shared/hooks/company.hooks"
import { BLUE, WHITE } from "App.scss"
import { getTooltipPopupContainer } from "Shared/utils/helpers"
import { parseHtml } from "Shared/utils/htmlParserHelper"

interface MileageMainDetailsSectionInterface {
    expense: Mileage
}

export const MileageMainDetailsSection: React.FC<MileageMainDetailsSectionInterface> = ({ expense }) => {
    const { t } = useTranslation()
    const company = useCompany()
    const distanceBuffer = company.mileageDistanceBuffer
    const showOverThresholdInfo = isDistanceOverThreshold(expense, distanceBuffer)

    const StopsList = () => (
        <Steps direction="vertical" size="small" current={expense.stops.length}>
            {expense.stops.map(({ address }, i, arr) => (
                // last item get reduced size for equal spacing
                <Steps.Step key={i} className={arr.length - 1 === i ? `max-h-24` : ``} status="process" title={<p className="text-black max-w-200 truncate">{address}</p>} />
            ))}
        </Steps>
    )

    const StopsView = () => (
        <div className="flex items-center justify-end">
            <div className="mb-8 mr-12">
                {expense.stops.length} {t("label:mileage.stops")}
            </div>
            <Tooltip getPopupContainer={getTooltipPopupContainer} title={StopsList} placement="topLeft" color={WHITE}>
                <div>
                    <Avatar.Group maxCount={4}>
                        {expense.stops.map((_stop, index) => (
                            <Avatar className="bg-gray-200" key={index}>
                                <MapPin color={BLUE} size={16} className="mt-6" />
                            </Avatar>
                        ))}
                    </Avatar.Group>
                </div>
            </Tooltip>
        </div>
    )

    return (
        <Card className="w-full">
            <Row gutter={[16, 24]}>
                <Col span={10}>
                    <div>{t("label:mileage.reason")}</div>
                    <div className="flex justify-start h-32 items-center">
                        <div className="leading-tight truncate text-base font-medium">{expense?.tripReason ? parseHtml(expense.tripReason) : t("label:n/a")}</div>
                    </div>
                </Col>
                {showOverThresholdInfo ? (
                    <Col span={9}>
                        <div>{t("label:mileage.additional_distance_reason")}</div>
                        <div className="flex justify-start h-32 items-center">
                            <div className="leading-tight truncate text-base font-medium">{expense?.additionalStopReason ?? t("label:n/a")}</div>
                        </div>
                    </Col>
                ) : (
                    <Col span={9} />
                )}

                <Col span={5}>
                    <StopsView />
                </Col>

                <Col span={10}>
                    <div>{t("label:mileage.vehicle")}</div>
                    <div className="flex justify-start h-32 items-center font-medium">
                        <div className="leading-tight truncate text-base">{getVehicleString(expense.vehicle, t)}</div>
                    </div>
                </Col>

                <Col span={9}>
                    <div>{t("label:mileage.date")}</div>
                    <div className="flex justify-start h-32 items-center">
                        <div className="leading-tight truncate text-base font-medium">
                            {expense.datePurchased ? moment(expense.datePurchased).format("DD.MM.YYYY") : t("label:n/a")}
                        </div>
                    </div>
                </Col>

                <Col span={5}>
                    <div className="flex justify-end text-right">
                        <div>{t("label:mileage.amount")}</div>
                    </div>
                    <div className="flex justify-end h-32 items-center font-medium">
                        <PriceLabel value={expense.totalGrossPrice} currency={expense.currency} interval={PriceIntervalEnum.ONE_TIME} />
                    </div>
                </Col>

                <Col span={10}>
                    <div>{t("label:mileage.start_address")}</div>
                    <div className="flex justify-start h-32 items-center font-medium">
                        <div className="leading-tight truncate text-base">{expense?.stops?.[0]?.address ?? t("label:n/a")}</div>
                    </div>
                </Col>

                <Col span={9}>
                    <div>{t("label:mileage.destination_address")}</div>
                    <div className="flex justify-start h-32 items-center font-medium">
                        <div className="leading-tight truncate text-base">{expense?.stops?.[(expense?.stops?.length ?? 1) - 1].address ?? t("label:n/a")}</div>
                    </div>
                </Col>

                <Col span={5}>
                    <div className="flex justify-end">
                        <div>{t("label:mileage.distance")}</div>
                    </div>
                    <div className="flex justify-end h-32 items-center font-medium">
                        <div className="flex leading-tight truncate text-base">
                            {expense?.actualDistance ?? 0} km
                            {showOverThresholdInfo ? <HoverableInfo type="warn" message={t("info:mileage.above_threshold", { distance: distanceBuffer })} /> : <></>}
                        </div>
                    </div>
                </Col>
            </Row>
        </Card>
    )
}
